/**
 * Chat Command Input
 *
 * Floating picker shell for prompt, file, tool, and mention commands.
 *
 * Primary responsibilities:
 * - Render the active command picker above the composer when space is tight
 * - Keep command menus out of the normal composer layout
 *
 * Dependencies:
 * - Chatbot UI context
 * - Prompt, file, tool, and unified mention pickers
 */

import { FC, useContext, useEffect, useRef, useState } from 'react'
import { ChatbotUIContext } from '@/context/context'
import { Tables } from '@/supabase/types'

import { cn } from '@/lib/utils'
import { MessageHistoryPicker } from '@/components/message-history-picker'

import { usePromptAndCommand } from './chat-hooks/use-prompt-and-command'
import { FilePicker } from './file-picker'
import { PromptPicker } from './prompt-picker'
import { ToolPicker } from './tool-picker'
import { UnifiedMentionPicker, type UnifiedMentionOption } from './unified-mention-picker'

interface ChatCommandInputProps {
  chatId?: string
  isMentionPickerEnabled?: boolean
  mentionOptions?: UnifiedMentionOption[]
  onSelectMention?: (option: UnifiedMentionOption) => void
  onUseAssistantAsActive?: (assistant: Tables<'assistants'>) => void
}

type CommandPickerLayout = {
  maxHeight: number
  placement: 'above' | 'below'
  top: number
  left: number
  width: number
}

const PICKER_MARGIN = 12
const PICKER_GAP = 8
const PICKER_MAX_HEIGHT = 300
const PICKER_MIN_HEIGHT = 112
const PICKER_MIN_WIDTH = 280

function measureCommandPicker(parent: HTMLElement): CommandPickerLayout {
  const rect = parent.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const availableWidth = Math.max(0, viewportWidth - PICKER_MARGIN * 2)
  const minWidth = Math.min(PICKER_MIN_WIDTH, availableWidth)
  const width = Math.min(Math.max(rect.width, minWidth), availableWidth)
  const maxLeft = Math.max(PICKER_MARGIN, viewportWidth - width - PICKER_MARGIN)
  const left = Math.min(Math.max(rect.left, PICKER_MARGIN), maxLeft)
  const availableAbove = Math.max(rect.top - PICKER_MARGIN - PICKER_GAP, 0)
  const availableBelow = Math.max(viewportHeight - rect.bottom - PICKER_MARGIN - PICKER_GAP, 0)
  const placement =
    availableAbove >= PICKER_MIN_HEIGHT || availableAbove >= availableBelow ? 'above' : 'below'
  const availableSpace = placement === 'above' ? availableAbove : availableBelow
  const maxHeight = Math.max(PICKER_MIN_HEIGHT, Math.min(PICKER_MAX_HEIGHT, availableSpace))

  return {
    maxHeight,
    placement,
    top:
      placement === 'above'
        ? Math.max(PICKER_MARGIN, rect.top - maxHeight - PICKER_GAP)
        : Math.min(viewportHeight - PICKER_MARGIN - PICKER_MIN_HEIGHT, rect.bottom + PICKER_GAP),
    left,
    width
  }
}

export const ChatCommandInput: FC<ChatCommandInputProps> = ({
  chatId,
  isMentionPickerEnabled = true,
  mentionOptions = [],
  onSelectMention = () => {},
  onUseAssistantAsActive
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [pickerLayout, setPickerLayout] = useState<CommandPickerLayout | null>(null)
  const {
    profile,
    newMessageFiles: contextNewMessageFiles,
    chatFiles: contextChatFiles,
    isFilePickerOpen,
    setIsFilePickerOpen,
    hashtagCommand,
    focusPrompt,
    focusFile,
    focusAssistant,
    atCommand,
    isPromptPickerOpen,
    isToolPickerOpen,
    isAssistantPickerOpen,
    setIsAssistantPickerOpen,
    isMessageHistoryPickerOpen,
    setIsPaywallOpen,
    getAttachmentStoreKey,
    getScopedAttachmentBindings
  } = useContext(ChatbotUIContext)
  const scopedAttachmentState = chatId
    ? getScopedAttachmentBindings(getAttachmentStoreKey(chatId)).getSnapshot()
    : null
  const newMessageFiles = scopedAttachmentState?.newMessageFiles ?? contextNewMessageFiles
  const chatFiles = scopedAttachmentState?.chatFiles ?? contextChatFiles

  const { handleSelectUserFile, handleSelectUserCollection } = usePromptAndCommand({
    chatId
  })
  const isMentionPickerOpen = isMentionPickerEnabled && isAssistantPickerOpen

  const isOpen =
    isPromptPickerOpen ||
    isToolPickerOpen ||
    isMentionPickerOpen ||
    isFilePickerOpen ||
    isMessageHistoryPickerOpen

  useEffect(() => {
    if (!isMentionPickerEnabled && isAssistantPickerOpen) {
      setIsAssistantPickerOpen(false)
    }
  }, [isAssistantPickerOpen, isMentionPickerEnabled, setIsAssistantPickerOpen])

  useEffect(() => {
    if (!isOpen) {
      setPickerLayout(null)
      return
    }

    const updatePickerLayout = () => {
      const parent = containerRef.current?.parentElement

      if (!parent) {
        return
      }

      setPickerLayout(measureCommandPicker(parent))
    }

    updatePickerLayout()
    window.addEventListener('resize', updatePickerLayout)
    window.addEventListener('scroll', updatePickerLayout, true)

    return () => {
      window.removeEventListener('resize', updatePickerLayout)
      window.removeEventListener('scroll', updatePickerLayout, true)
    }
  }, [isOpen])

  return (
    <div
      ref={containerRef}
      className={cn(
        'border-border bg-background dark:border-border fixed z-[2147483000] min-w-0 overflow-hidden rounded-xl border shadow-xl',
        isOpen && pickerLayout ? 'flex flex-col' : 'hidden'
      )}
      style={
        pickerLayout
          ? {
              left: pickerLayout.left,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: pickerLayout.maxHeight,
              top: pickerLayout.top,
              width: pickerLayout.width
            }
          : undefined
      }
    >
      <PromptPicker />

      <FilePicker
        isOpen={isFilePickerOpen}
        searchQuery={hashtagCommand}
        onOpenChange={setIsFilePickerOpen}
        selectedFileIds={[...newMessageFiles, ...chatFiles].map((file) => file.id)}
        selectedCollectionIds={[]}
        onSelectFile={handleSelectUserFile}
        onSelectCollection={handleSelectUserCollection}
        isFocused={focusFile}
      />
      <ToolPicker />

      <UnifiedMentionPicker
        isOpen={isMentionPickerOpen}
        searchQuery={atCommand}
        options={mentionOptions}
        focusSignal={focusAssistant}
        profile={profile}
        onOpenChange={setIsAssistantPickerOpen}
        onSelectMention={onSelectMention}
        onUseAssistantAsActive={onUseAssistantAsActive}
        onRequestPaywall={setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined}
      />
    </div>
  )
}
